// tabs/CourseFilesTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Group, Text, SimpleGrid, Divider, ActionIcon, Tooltip,
} from '@mantine/core';
import { IconTrash, IconEye } from '@tabler/icons-react';
import { useRegistrationService, RegistrationFile } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FileUploader } from '@/components/ccms-components/file-management';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import type { ViewerFile } from '@/components/ccms-components/file-management/types';
import { getFileIcon, formatUploadedDate } from '@components/ccms-components/utils/fileDisplay';
import ConfirmActionModal from './ConfirmActionModal';

// ─── Props ────────────────────────────────────────────────────────────────────
interface CourseFilesTabProps {
  registrationId: number;
  active: boolean;
  onRegistrationChange?: () => void;
}

// ─── Completed upload shape returned by FileUploader ──────────────────────────
interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

// ─── CourseFilesTab ────────────────────────────────────────────────────────────
export default function CourseFilesTab({ registrationId, active, onRegistrationChange }: CourseFilesTabProps) {
  const registrationService = useRegistrationService();

  const [documents, setDocuments] = useState<RegistrationFile[]>([]);
  const [loading, setLoading]     = useState(false);

  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Universal file viewer (preview) ────────────────────────────────────────
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // ⚠️ ASSUMPTION: RegistrationFile has no size field — fileSize hardcoded to 0.
  const viewerFiles: ViewerFile[] = useMemo(
    () =>
      documents.map((doc) => ({
        id: String(doc.fileID),
        fileName: doc.name ?? `File ${doc.fileID}`,
        mimeType: doc.type ?? 'application/octet-stream',
        fileSize: 0,
        url: doc.url ?? undefined,
      })),
    [documents]
  );

  const openPreview = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // ── Fetch files ────────────────────────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const result = await registrationService.getFiles(registrationId);
      setDocuments(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [registrationId, registrationService]);

  useEffect(() => {
    if (active) fetchDocuments();
  }, [active, fetchDocuments]);

  // ── Upload complete handler ────────────────────────────────────────────────
  const handleUploadComplete = async (files: CompletedUpload[]) => {
    try {
      await Promise.all(
        files.map((file) =>
          registrationService.addFile(registrationId, {
            registrationID: registrationId,
            name: file.fileName,
            url:  file.url,
            type: file.mimeType,
          } as any)
        )
      );
      await fetchDocuments();
      onRegistrationChange?.();
      toast.success(
        'File Added',
        `${files.length} file${files.length !== 1 ? 's' : ''} uploaded successfully.`
      );
    } catch (e) {
      console.error(e);
      toast.error('Error', 'File uploaded but failed to attach it to the registration.');
    }
  };

  // ── Remove file — opens confirm modal ─────────────────────────────────────
  const handleRemoveDocument = (doc: RegistrationFile) => {
    setConfirmMessage(`Remove "${doc.name}" from this registration?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await registrationService.deleteFile(registrationId, doc.fileID);
        await fetchDocuments();
        onRegistrationChange?.();
        toast.warning('File Removed', `"${doc.name}" has been removed.`);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove file.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove File"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        files={viewerFiles}
        initialIndex={viewerIndex}
        onIndexChange={setViewerIndex}
      />

      <FileUploader
        domain="Registration"
        recordId={String(registrationId)}
        multiple
        accept={['*/*']}
        onUploadComplete={handleUploadComplete}
        onUploadError={(err: any) => {
          console.error(err);
          toast.error('Upload Failed', err?.message ?? 'Could not upload file.');
        }}
      />

      <Divider my="md" />

      {documents.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No files uploaded yet.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {documents.map((doc, index) => {
            const FileIcon = getFileIcon(doc.type);

            return (
              <Card key={doc.fileID} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                    <FileIcon size={28} />

                    <div style={{ overflow: 'hidden' }}>
                      <Text fw={700} truncate>{doc.name}</Text>
                      <Text size="xs" c="dimmed">
                        Uploaded: {formatUploadedDate((doc as any).uploadedDateTime)}
                      </Text>
                    </div>
                  </Group>

                  <Group gap={4} wrap="nowrap">
                    {doc.url && (
                      <Tooltip label="Preview">
                        <ActionIcon variant="light" color="blue" onClick={() => openPreview(index)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => handleRemoveDocument(doc)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </>
  );
}